import { BinaryReader, BinaryWriter } from "@bufbuild/protobuf/wire";
import { Timestamp } from "../../google/protobuf/timestamp";
export const protobufPackage = "com.snap.camerakit.v3";
export interface OperationalMetric {
    name: string;
    timestamp: Date | undefined;
    metric?: {
        $case: "count";
        count: string;
    } | {
        $case: "latencyMillis";
        latencyMillis: string;
    } | {
        $case: "histogram";
        histogram: string;
    } | undefined;
}
export interface OperationalMetricsBundle {
    metrics: OperationalMetric[];
}
function createBaseOperationalMetric(): OperationalMetric {
    return { name: "", timestamp: undefined, metric: undefined };
}
export const OperationalMetric: MessageFns<OperationalMetric> = {
    encode(message: OperationalMetric, writer: BinaryWriter = new BinaryWriter()): BinaryWriter {
        if (message.name !== "") {
            writer.uint32(10).string(message.name);
        }
        if (message.timestamp !== undefined) {
            Timestamp.encode(toTimestamp(message.timestamp), writer.uint32(18).fork()).join();
        }
        switch (message.metric?.$case) {
            case "count":
                writer.uint32(24).uint64(message.metric.count);
                break;
            case "latencyMillis":
                writer.uint32(32).uint64(message.metric.latencyMillis);
                break;
            case "histogram":
                writer.uint32(40).int64(message.metric.histogram);
                break;
        }
        return writer;
    },
    decode(input: BinaryReader | Uint8Array, length?: number): OperationalMetric {
        const reader = input instanceof BinaryReader ? input : new BinaryReader(input);
        let end = length === undefined ? reader.len : reader.pos + length;
        const message = createBaseOperationalMetric();
        while (reader.pos < end) {
            const tag = reader.uint32();
            switch (tag >>> 3) {
                case 1: {
                    if (tag !== 10) {
                        break;
                    }
                    message.name = reader.string();
                    continue;
                }
                case 2: {
                    if (tag !== 18) {
                        break;
                    }
                    message.timestamp = fromTimestamp(Timestamp.decode(reader, reader.uint32()));
                    continue;
                }
                case 3: {
                    if (tag !== 24) {
                        break;
                    }
                    message.metric = { $case: "count", count: reader.uint64().toString() };
                    continue;
                }
                case 4: {
                    if (tag !== 32) {
                        break;
                    }
                    message.metric = { $case: "latencyMillis", latencyMillis: reader.uint64().toString() };
                    continue;
                }
                case 5: {
                    if (tag !== 40) {
                        break;
                    }
                    message.metric = { $case: "histogram", histogram: reader.int64().toString() };
                    continue;
                }
            }
            if ((tag & 7) === 4 || tag === 0) {
                break;
            }
            reader.skip(tag & 7);
        }
        return message;
    },
    create<I extends Exact<DeepPartial<OperationalMetric>, I>>(base?: I): OperationalMetric {
        return OperationalMetric.fromPartial(base ?? ({} as any));
    },
    fromPartial<I extends Exact<DeepPartial<OperationalMetric>, I>>(object: I): OperationalMetric {
        const message = createBaseOperationalMetric();
        message.name = object.name ?? "";
        message.timestamp = object.timestamp ?? undefined;
        if (object.metric?.$case === "count" && object.metric?.count !== undefined && object.metric?.count !== null) {
            message.metric = { $case: "count", count: object.metric.count };
        }
        if (object.metric?.$case === "latencyMillis" &&
            object.metric?.latencyMillis !== undefined &&
            object.metric?.latencyMillis !== null) {
            message.metric = { $case: "latencyMillis", latencyMillis: object.metric.latencyMillis };
        }
        if (object.metric?.$case === "histogram" &&
            object.metric?.histogram !== undefined &&
            object.metric?.histogram !== null) {
            message.metric = { $case: "histogram", histogram: object.metric.histogram };
        }
        return message;
    },
};
function createBaseOperationalMetricsBundle(): OperationalMetricsBundle {
    return { metrics: [] };
}
export const OperationalMetricsBundle: MessageFns<OperationalMetricsBundle> = {
    encode(message: OperationalMetricsBundle, writer: BinaryWriter = new BinaryWriter()): BinaryWriter {
        for (const v of message.metrics) {
            OperationalMetric.encode(v!, writer.uint32(10).fork()).join();
        }
        return writer;
    },
    decode(input: BinaryReader | Uint8Array, length?: number): OperationalMetricsBundle {
        const reader = input instanceof BinaryReader ? input : new BinaryReader(input);
        let end = length === undefined ? reader.len : reader.pos + length;
        const message = createBaseOperationalMetricsBundle();
        while (reader.pos < end) {
            const tag = reader.uint32();
            switch (tag >>> 3) {
                case 1: {
                    if (tag !== 10) {
                        break;
                    }
                    message.metrics.push(OperationalMetric.decode(reader, reader.uint32()));
                    continue;
                }
            }
            if ((tag & 7) === 4 || tag === 0) {
                break;
            }
            reader.skip(tag & 7);
        }
        return message;
    },
    create<I extends Exact<DeepPartial<OperationalMetricsBundle>, I>>(base?: I): OperationalMetricsBundle {
        return OperationalMetricsBundle.fromPartial(base ?? ({} as any));
    },
    fromPartial<I extends Exact<DeepPartial<OperationalMetricsBundle>, I>>(object: I): OperationalMetricsBundle {
        const message = createBaseOperationalMetricsBundle();
        message.metrics = object.metrics?.map((e) => OperationalMetric.fromPartial(e)) || [];
        return message;
    },
};
type Builtin = Date | Function | Uint8Array | string | number | boolean | undefined;
export type DeepPartial<T> = T extends Builtin ? T : T extends globalThis.Array<infer U> ? globalThis.Array<DeepPartial<U>> : T extends ReadonlyArray<infer U> ? ReadonlyArray<DeepPartial<U>> : T extends {
    $case: string;
} ? {
    [K in keyof Omit<T, "$case">]?: DeepPartial<T[K]>;
} & {
    $case: T["$case"];
} : T extends {} ? {
    [K in keyof T]?: DeepPartial<T[K]>;
} : Partial<T>;
type KeysOfUnion<T> = T extends T ? keyof T : never;
export type Exact<P, I extends P> = P extends Builtin ? P : P & {
    [K in keyof P]: Exact<P[K], I[K]>;
} & {
    [K in Exclude<keyof I, KeysOfUnion<P>>]: never;
};
function toTimestamp(date: Date): Timestamp {
    const seconds = Math.trunc(date.getTime() / 1000).toString();
    const nanos = (date.getTime() % 1000) * 1000000;
    return { seconds, nanos };
}
function fromTimestamp(t: Timestamp): Date {
    let millis = (globalThis.Number(t.seconds) || 0) * 1000;
    millis += (t.nanos || 0) / 1000000;
    return new globalThis.Date(millis);
}
export interface MessageFns<T> {
    encode(message: T, writer?: BinaryWriter): BinaryWriter;
    decode(input: BinaryReader | Uint8Array, length?: number): T;
    create<I extends Exact<DeepPartial<T>, I>>(base?: I): T;
    fromPartial<I extends Exact<DeepPartial<T>, I>>(object: I): T;
}
